import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  Timestamp,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { withFirebaseErrorHandling, retryFirebaseOperation } from '../errors/firebaseErrors';
import { TestMetadata, TEST_METADATA_COLLECTION } from '../../types/testMetadata';
import { TestMetadataService } from './testMetadataService';
import { OpticalAnswer } from './opticalAnswerService';
import { validateAndCleanIntervalData } from '../../utils/intervalValidation';

// Matches BulkSolutionCreateDto structure
export interface FirebaseAnswerData {
  // Required fields
  studyTaskId: string;
  studentAnswer?: OpticalAnswer;
  trajectory?: string; // Drawing trajectory data
  eliminationTrajectory?: string; // Elimination marks trajectory
  intervals?: string; // Time intervals data
  processed: boolean;
  proficiencyInOutcome?: number;
  isSkipped: boolean;
  isEvaluate: boolean;
  userAccountId: string;
  questionId: string;
  
  // Additional tracking fields
  testId: string;
  tenantId: string;
  
  // Sync tracking fields
  syncedToMssql: boolean;
  syncAttempts: number;
  lastSyncAttempt?: Timestamp;
  syncError?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// TestMetadata interface is now imported from types/testMetadata.ts

// DTO for bulk submission to MSSQL
export interface BulkSolutionCreateDto {
  studyTaskId: string;
  solutions: Array<{
    questionId: string;
    studentAnswer?: string;
    trajectory?: string;
    eliminationTrajectory?: string;
    intervals?: string;
    processed: boolean;
    proficiencyInOutcome?: number;
    isSkipped: boolean;
    isEvaluate: boolean;
  }>;
}

export class AnswerDataService {
  private static ANSWERS_COLLECTION = 'answers';
  private static TEST_METADATA_COLLECTION = TEST_METADATA_COLLECTION;
  
  // REMOVED: createTestMetadata - now handled by TestMetadataService/WhiteboardProvider
  
  /**
   * Save or update a single answer
   */
  static async saveAnswer(
    testId: string,
    studyTaskId: string,
    questionId: string,
    userId: string,
    tenantId: string,
    data: {
      studentAnswer?: OpticalAnswer;
      trajectory?: string;
      eliminationTrajectory?: string;
      intervals?: string;
      totalDuration?: number;
      isSkipped?: boolean;
      isEvaluate?: boolean;
      proficiencyInOutcome?: number;
    }
  ): Promise<void> {
    // Check Firebase authentication
    const { getCurrentFirebaseUser } = await import('./authService');
    const currentUser = getCurrentFirebaseUser();
    
    if (!currentUser) {
      console.error('🚫 Firebase user not authenticated when saving answer');
      console.error('Attempting to save for userId:', userId);
      throw new Error('User must be authenticated to save answers');
    }
    
    // console.log('✅ Firebase user authenticated:', currentUser.uid);
    
    return retryFirebaseOperation(async () => {
      const answerKey = `${tenantId}_${testId}_${userId}_${questionId}`;
      const answerRef = doc(db, this.ANSWERS_COLLECTION, answerKey);
      
      // Document key for answer prepared
     
      
      // Validate and clean interval data
      let cleanedIntervals = data.intervals;
      if (data.intervals) {
        const validation = validateAndCleanIntervalData(data.intervals);
        
        if (validation.isValid) {
          cleanedIntervals = validation.cleanedData || undefined;
          
          if (validation.warnings.length > 0) {
            console.warn('⚠️ Interval validation warnings:', validation.warnings);
          }
          
    // console.log('✅ Interval validation passed:', validation.stats);
        } else {
          console.error('❌ Interval validation failed:', validation.errors);
          // Use original data but log the issues
          cleanedIntervals = data.intervals;
        }
      }

      // Clean undefined values for Firebase
      const cleanData = Object.entries({
        ...data,
        intervals: cleanedIntervals
      }).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
      
      const answerData: Partial<FirebaseAnswerData> = {
        studyTaskId,
        testId,
        tenantId,
        userAccountId: userId,
        questionId,
        ...cleanData,
        processed: false,
        syncedToMssql: false,
        syncAttempts: 0,
        updatedAt: serverTimestamp() as Timestamp
      };
      
      // Check if document exists
      const docSnap = await getDoc(answerRef);
      const isNewAnswer = !docSnap.exists();
      
      if (docSnap.exists()) {
      
        await updateDoc(answerRef, answerData);
      } else {
       
        await setDoc(answerRef, {
          ...answerData,
          createdAt: serverTimestamp(),
          isSkipped: data.isSkipped ?? false,
          isEvaluate: data.isEvaluate ?? true
        });
      }
    
      
      // Update test metadata answered count only for new answers
      if (isNewAnswer && data.studentAnswer) {
        await this.updateAnsweredCount(testId, tenantId);
      }
    
    }, 3, 'saveAnswer');
  }
  
  /**
   * Get all answers for a test
   * @param testId - Test ID (ALWAYS use this - in normal mode: qbTestId, in WebView: resourceId)
   * @param userId - User ID
   * @param tenantId - Tenant ID
   * @param studyTaskId - DEPRECATED: Only kept for backward compatibility, not used for querying
   */
  static async getTestAnswers(
    testId: string,
    userId: string,
    tenantId: string,
    studyTaskId?: string
  ): Promise<FirebaseAnswerData[]> {
    try {
      console.log('🔍 [getTestAnswers] Querying with:', { testId, userId, tenantId, studyTaskId });

      // IMPORTANT: Always query ONLY by testId
      // - Normal mode: testId = qbTestId
      // - WebView mode: testId = resourceId (same thing)
      // studyTaskId is stored as a field but NEVER used for querying
      const q = query(
        collection(db, this.ANSWERS_COLLECTION),
        where('testId', '==', testId),
        where('userAccountId', '==', userId),
        where('tenantId', '==', tenantId)
      );
      
      const querySnapshot = await getDocs(q);
      const answers: FirebaseAnswerData[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data() as FirebaseAnswerData;
        answers.push(data);
        console.log(`📄 Answer doc: ${doc.id}, questionId: ${data.questionId}, testId: ${data.testId}, studyTaskId: ${data.studyTaskId}`);
      });

      console.log(`📊 Found ${answers.length} answers for testId: ${testId}${studyTaskId ? ` (studyTaskId ignored: ${studyTaskId})` : ''}`);

      // ✅ REMOVED: studyTaskId fallback logic
      // We ONLY query by testId now. studyTaskId is just a stored field, not for querying.
      
      // If still no answers and we have a potential alternative ID pattern, try one more time
      // This handles cases where the user has both studyTask.id and studyTask.resourceId
      if (answers.length === 0) {
        // Try to find any answers for this user and tenant to debug
        const debugQuery = query(
          collection(db, this.ANSWERS_COLLECTION),
          where('userAccountId', '==', userId),
          where('tenantId', '==', tenantId)
        );
        
        const debugSnapshot = await getDocs(debugQuery);
        const allUserAnswers: string[] = [];
        
        debugSnapshot.forEach((doc) => {
          const data = doc.data() as FirebaseAnswerData;
          if (!allUserAnswers.includes(data.testId)) {
            allUserAnswers.push(data.testId);
          }
        });
        
        if (allUserAnswers.length > 0) {
          console.warn(`⚠️ No answers found for testId: ${testId} or studyTaskId: ${studyTaskId}`);
          console.warn(`📋 But user has answers for these testIds:`, allUserAnswers);
          console.warn(`💡 This suggests a mismatch in how testId is being set. Check if studyTask.id vs studyTask.resourceId is consistent.`);
          console.warn(`🔧 Query parameters used:`, { testId, studyTaskId, userId, tenantId });
        } else {
          console.warn(`⚠️ No answers found for any test for this user`);
          console.warn(`🔧 Query parameters used:`, { testId, studyTaskId, userId, tenantId });
        }
      }
      
      return answers;
    } catch (error) {
      console.error('Error getting test answers:', error);
      return [];
    }
  }
  
  // REMOVED: getTestMetadata - now handled by TestMetadataService
  
  // REMOVED: updateTestMetadata - now handled by TestMetadataService
  
  /**
   * Update answered questions count
   */
  private static async updateAnsweredCount(
    testId: string,
    tenantId: string
  ): Promise<void> {
    try {
      const metadataRef = doc(db, this.TEST_METADATA_COLLECTION, `${tenantId}_${testId}`);
      const metadata = await TestMetadataService.getTestMetadata(testId, tenantId);
      
      if (metadata) {
        // Instead of complex query, just increment the counter
        // This is more efficient and doesn't require an index
        const currentCount = metadata.answeredQuestions || 0;
        
        await updateDoc(metadataRef, {
          answeredQuestions: currentCount + 1,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating answered count:', error);
    }
  }
  
  /**
   * Mark test as completed and prepare for bulk submission
   */
  static async completeTest(
    testId: string,
    tenantId: string
  ): Promise<BulkSolutionCreateDto | null> {
    return retryFirebaseOperation(async () => {
      const metadataRef = doc(db, this.TEST_METADATA_COLLECTION, `${tenantId}_${testId}`);
      const metadata = await TestMetadataService.getTestMetadata(testId, tenantId);
      
      if (!metadata) {
        throw new Error('Test metadata not found');
      }
      
      // Update test status
      await updateDoc(metadataRef, {
        status: 'completed',
        endTime: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Get all answers for bulk submission
      const answers = await this.getTestAnswers(testId, metadata.userId.toString(), tenantId);
      
      // Prepare bulk submission DTO
      const bulkDto: BulkSolutionCreateDto = {
        studyTaskId: metadata.studyTaskId || testId,
        solutions: answers.map(answer => ({
          questionId: answer.questionId,
          studentAnswer: answer.studentAnswer,
          trajectory: answer.trajectory,
          eliminationTrajectory: answer.eliminationTrajectory,
          intervals: answer.intervals,
          processed: answer.processed,
          proficiencyInOutcome: answer.proficiencyInOutcome,
          isSkipped: answer.isSkipped,
          isEvaluate: answer.isEvaluate
        }))
      };
      
      return bulkDto;
    }, 3, 'completeTest');
  }
  
  /**
   * Mark answers as synced to MSSQL
   */
  static async markAnswersAsSynced(
    testId: string,
    userId: string,
    tenantId: string,
    success: boolean,
    error?: string
  ): Promise<void> {
    return retryFirebaseOperation(async () => {
      const batch = writeBatch(db);
      
      // Update test metadata
      const metadataRef = doc(db, this.TEST_METADATA_COLLECTION, `${tenantId}_${testId}`);
      const metadata = await TestMetadataService.getTestMetadata(testId, tenantId);
      
      if (metadata) {
        batch.update(metadataRef, {
          status: success ? 'synced' : 'sync_failed',
          syncedToMssql: success,
          syncAttempts: (metadata.syncAttempts || 0) + 1,
          lastSyncAttempt: serverTimestamp(),
          syncError: error || null,
          updatedAt: serverTimestamp()
        });
      }
      
      // Update individual answers
      const q = query(
        collection(db, this.ANSWERS_COLLECTION),
        where('testId', '==', testId),
        where('userAccountId', '==', userId),
        where('tenantId', '==', tenantId)
      );
      
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, {
          syncedToMssql: success,
          syncAttempts: doc.data().syncAttempts + 1,
          lastSyncAttempt: serverTimestamp(),
          syncError: error || null,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    }, 3, 'markAnswersAsSynced');
  }
  
  /**
   * Get failed sync tests for retry
   */
  static async getFailedSyncTests(tenantId: string): Promise<TestMetadata[]> {
    try {
      const q = query(
        collection(db, this.TEST_METADATA_COLLECTION),
        where('tenantId', '==', tenantId),
        where('status', '==', 'sync_failed'),
        where('syncAttempts', '<', 5) // Max 5 retry attempts
      );
      
      const querySnapshot = await getDocs(q);
      const failedTests: TestMetadata[] = [];
      
      querySnapshot.forEach((doc) => {
        failedTests.push(doc.data() as TestMetadata);
      });
      
      return failedTests;
    } catch (error) {
      console.error('Error getting failed sync tests:', error);
      return [];
    }
  }
  
  /**
   * Load test answers and return as a Record for optical form
   */
  static async loadTestAnswers(
    testId: string,
    userId: string,
    tenantId: string,
    studyTaskId?: string
  ): Promise<Record<string, OpticalAnswer>> {
    try {
      // Use getTestAnswers which now includes fallback logic
      const answers = await this.getTestAnswers(testId, userId, tenantId, studyTaskId);
      const opticalAnswers: Record<string, OpticalAnswer> = {};
      
      answers.forEach(answer => {
        if (answer.studentAnswer && answer.questionId) {
          opticalAnswers[answer.questionId] = answer.studentAnswer;
        }
      });
      
    // console.log(`📦 Loaded ${Object.keys(opticalAnswers).length} optical answers for test`);
      
      return opticalAnswers;
    } catch (error) {
      console.error('Error loading test answers:', error);
      return {};
    }
  }
}